import React from 'react';
import { motion } from 'framer-motion';
import { Star, ShieldCheck, Zap, Target, ArrowRight } from 'lucide-react';
import heroImg from '../assets/hero.webp';
import sarahImg from '../assets/sarah.webp';
import content from '../data/content.json';

const Hero = ({ data }) => {
    return (
        <section className="relative h-screen min-h-[750px] w-full flex items-center overflow-hidden bg-primary">

            {/* Split Layout Container */}
            <div className="absolute inset-0 flex flex-col lg:flex-row">
                {/* Left: Solid Content Area - Matte Charcoal with Ambient Glow */}
                <div className="w-full lg:w-[25%] h-[50%] lg:h-full bg-[#1a1b1e] z-10 relative overflow-hidden">
                    {/* Subtle Neon Accents - Static Glow (Low Intensity) */}
                    <div className="absolute inset-0 z-0 pointer-events-none">
                        <div className="absolute left-1/2 top-0 -translate-x-1/2 w-[600px] h-[180px] bg-blue-600/10 blur-3xl rounded-full"></div>
                        <div className="absolute right-0 bottom-0 w-[320px] h-[120px] bg-accent/10 blur-2xl rounded-full"></div>
                        <div className="absolute left-0 bottom-0 w-[220px] h-[80px] bg-secondary/10 blur-2xl rounded-full"></div>
                    </div>

                    {/* Seam Blender: Forces the edge back to pure dark to match the right panel perfectly */}
                    <div className="absolute top-0 right-0 w-40 h-full bg-gradient-to-l from-[#1a1b1e] to-transparent pointer-events-none z-20" />
                </div>

                {/* Right: Sharp Property Image (No Blur) */}
                <div className="w-full lg:w-[75%] h-[50%] lg:h-full relative">
                    {/* Responsive Image Loading for LCP Optimization */}
                    <img
                        src={heroImg}
                        srcSet={`${heroImg} 1920w, ${heroImg} 800w`}
                        sizes="(max-width: 768px) 800px, 1920px"
                        alt="Luxury Estate"
                        width="1920"
                        height="1080"
                        fetchPriority="high"
                        loading="eager"
                        className="w-full h-full object-cover"
                    />
                    {/* Seamless blend gradient - Responsive Direction with enhanced mobile blending */}
                    <div className="absolute inset-0 bg-gradient-to-b lg:bg-gradient-to-r from-[#1a1b1e] via-[#1a1b1e]/60 via-30% to-transparent lg:from-[#1a1b1e] lg:via-[#1a1b1e]/40 lg:via-40%"></div>
                </div>
            </div>

            <div className="container max-w-7xl relative z-20 mx-auto px-6 lg:px-12 lg:pt-24 pt-16">
                <div className="max-w-4xl">



                    {/* Massive Decision-Maker Heading */}
                    <motion.h1
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1, duration: 0.6 }}
                        className="text-5xl md:text-6xl lg:text-[5rem] font-black leading-[0.9] text-white tracking-tighter mb-10"
                        dangerouslySetInnerHTML={{ __html: data.title }}
                    />

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2, duration: 0.6 }}
                        className="text-md md:text-lg text-white/70 mb-6 lg:mb-12 leading-relaxed max-w-xl text-white"
                    >
                        {data.subtitle}
                    </motion.p>

                    {/* Performance Hooks - High Contrast Row */}
                    {/* Performance Hooks - Sleek Horizontal Pills */}
                    <div className="flex flex-wrap gap-3 mb-12">
                        {data.hooks.map((hook, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.4 + (i * 0.1) }}
                                whileHover={{ scale: 1.05, backgroundColor: "rgba(255, 255, 255, 0.1)" }}
                                className="flex items-center gap-3 px-5 py-2.5 rounded-full border border-white/10 bg-white/5 backdrop-blur-sm cursor-default transition-all"
                            >
                                <div className="text-accent">
                                    {i === 0 ? <Target size={16} /> : i === 1 ? <Zap size={16} /> : <ShieldCheck size={16} />}
                                </div>
                                <span className="text-[0.65rem] font-bold uppercase tracking-widest text-white/90">{hook}</span>
                            </motion.div>
                        ))}
                    </div>

                    {/* Call to Action & Trust Signal - High Impact & Spaced */}
                    {/* Call to Action & Trust Signal - High Impact & Spaced */}
                    <div className="flex flex-col sm:flex-row gap-8 items-start mt-4">
                        <motion.button
                            initial={{ scale: 1 }}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            aria-label="Get my instant home valuation"
                            onClick={() => document.getElementById('value')?.scrollIntoView({ behavior: 'smooth' })}
                            className="group relative bg-white text-primary pl-8 pr-2 py-2 rounded-full text-sm font-black uppercase tracking-widest shadow-[0_0_30px_rgba(255,255,255,0.1)] hover:shadow-[0_0_50px_rgba(21,161,250,0.3)] transition-all duration-500 overflow-hidden flex items-center gap-4 cursor-pointer"
                        >
                            <div className="absolute inset-0 bg-accent translate-y-[101%] group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.19,1,0.22,1)]" />

                            <span className="relative z-10 group-hover:text-white transition-colors duration-300">
                                {data.cta}
                            </span>

                            <div className="relative z-10 w-10 h-10 bg-primary text-white rounded-full flex items-center justify-center group-hover:bg-white group-hover:text-primary transition-all duration-300 shadow-md group-hover:rotate-45">
                                <ArrowRight size={18} />
                            </div>
                        </motion.button>

                        {/* Trust Signal with Vertical Separator */}
                        <div className="flex items-center gap-6 group cursor-default">
                            <div className="w-[1px] h-10 bg-gradient-to-b from-transparent via-white/20 to-transparent hidden sm:block" />

                            <div className="flex flex-col gap-1.5">
                                <div className="flex gap-1">
                                    {[...Array(5)].map((_, i) => (
                                        <motion.div
                                            key={i}
                                            initial={{ opacity: 0, scale: 0 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            transition={{ delay: 0.5 + (i * 0.1) }}
                                        >
                                            <Star size={16} className="fill-accent text-accent drop-shadow-[0_0_8px_rgba(21,161,250,0.5)]" />
                                        </motion.div>
                                    ))}
                                </div>
                                <p className="text-[0.6rem] font-bold uppercase tracking-[0.25em] text-white/60 group-hover:text-white transition-colors">
                                    <span className="text-white">{data.stats?.rating || '4.8/5'}</span> {data.stats?.families || 'from 127+ Families'}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Floating Trust Element (Volume Sold) - REPOSITIONED ABOVE CARD */}
            <motion.div
                initial={{ opacity: 0, rotate: 10, scale: 0.9 }}
                animate={{ opacity: 1, rotate: -5, scale: 1 }}
                transition={{ delay: 1, duration: 1 }}
                className="hidden xl:block absolute right-24 bottom-[440px] z-30 bg-accent text-white px-8 py-5 rounded-[24px] shadow-[0_20px_50px_rgba(21,161,250,0.4)]"
            >
                <p className="text-3xl font-black leading-none">{data.stats?.volumeValue || '$450M+'}</p>
                <p className="text-[0.6rem] font-black uppercase tracking-widest opacity-80 mt-1">{data.stats?.volume || 'Volume Sold in Hartford'}</p>
            </motion.div>

            {/* Sarah Mitchell Personal Touch Card - REPOSITIONED TO BOTTOM RIGHT */}
            <motion.div
                initial={{ opacity: 0, scale: 0.9, x: 20 }}
                animate={{ opacity: 1, scale: 1, x: 0 }}
                transition={{ delay: 1, duration: 0.8 }}
                whileHover={{ y: -5 }}
                className="hidden lg:block absolute right-20 bottom-24 z-30 max-w-[260px]"
            >
                <div className="bg-white/90 backdrop-blur-xl p-5 rounded-[24px] shadow-2xl border border-white/40 group cursor-pointer hover:bg-white transition-all duration-300 relative overflow-hidden hover:shadow-[0_20px_40px_rgba(0,0,0,0.1)]">

                    {/* Live Indicator Pulse */}
                    <div className="absolute top-4 right-4 flex h-2.5 w-2.5">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500"></span>
                    </div>

                    <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 rounded-full p-[2px] bg-gradient-to-tr from-accent to-blue-600">
                            <img src={sarahImg} alt="Sarah Mitchell" width="48" height="48" className="w-full h-full rounded-full object-cover border-2 border-white" />
                        </div>
                        <div>
                            <p className="font-black text-primary uppercase text-[0.7rem] tracking-tighter leading-none mb-1">{data.card?.name || 'Sarah Mitchell'}</p>
                            <p className="text-[0.55rem] text-slate-400 font-bold uppercase tracking-wider">{data.card?.role || 'Top 1% Agent'}</p>
                        </div>
                    </div>

                    <p className="text-[0.75rem] text-slate-600 leading-relaxed font-semibold italic mb-4">
                        "Your home is your largest asset. Let's maximize your win together."
                    </p>

                    <div className="w-full h-[1px] bg-slate-100 mb-3" />

                    <button
                        onClick={() => {
                            const message = content.smsMessage || `Hi ${content.nav?.brand?.first || 'Sarah'}! I'm interested in your real estate services.`;
                            // Smart Logic for Desktop
                            if (window.innerWidth >= 768 && window.BrevoConversations) {
                                navigator.clipboard.writeText(message).catch(console.error);
                                window.BrevoConversations.openChat(true);
                            } else {
                                // Mobile (or Desktop fallback): Open SMS
                                const smsUrl = `sms:${content.contact.phoneSms}?body=${encodeURIComponent(message)}`;
                                window.location.href = smsUrl;
                            }
                        }}
                        className="w-full flex items-center justify-between group/text cursor-pointer bg-transparent border-0 p-0 text-left"
                        aria-label="Contact Agent"
                    >
                        <span className="text-[0.6rem] font-black uppercase tracking-widest text-accent group-hover/text:translate-x-1 transition-transform">
                            Ask a Question
                        </span>
                        <div className="w-6 h-6 rounded-full bg-accent/10 flex items-center justify-center text-accent group-hover:bg-accent group-hover/link:text-white transition-colors">
                            <ArrowRight size={12} />
                        </div>
                    </button>
                </div>
            </motion.div>

        </section>
    );
};

export default Hero;
